import { Box, IconButton, Typography } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import { FC, useState } from "react";
import { MoreHorizontal } from "react-feather";
import { EmployerFundLedger } from "../../../models/funding/employerFundLedger";
import { EmployerFundLedgerType } from "../../../models/funding/employerFundLedgerType";
import { formatCurrency, formatDate } from "../../../utils/formatters";
import { toTitleCase } from "../../../utils/toTitleCase";
import { useData } from "../../../utils/useData";
import { LoadingSpinner } from "../../shared/LoadingSpinner";
import { Table } from "../../shared/Table";
import { CardIssueInfoSideDrawer } from "./SideDrawers/CardIssueInfoSideDrawer";
import LoadFeeInfoSideDrawer from "./SideDrawers/LoadFeeInfoSideDrawer";
import { MonthlyUserFeeInfoSideDrawer } from "./SideDrawers/MonthlyUserFeeInfoSideDrawer";
import { TransactionInfoSideDrawer } from "./SideDrawers/TransactionInfoSideDrawer";

const EmployerFundLedgerTable: FC<{
  ledgers: EmployerFundLedger[];
  totalItems: number;
  page: number;
  pageSize: number;
  updatePage: (value: number) => void;
  updatePageSize: (value: number) => void;
  isLoading: boolean;
  displayTransactionInfoMessage?: boolean;
}> = ({
  ledgers,
  totalItems,
  page,
  pageSize,
  updatePage,
  updatePageSize,
  isLoading,
  displayTransactionInfoMessage
}) => {
  const { employer, employees, accounts, serviceSectors } = useData();

  const [isTransactionInfoOpen, setIsTransactionInfoOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | null
  >(null);

  const [isLoadFeeInfoOpen, setIsLoadFeeInfoOpen] = useState(false);
  const [selectedLoadFeeLedger, setSelectedLoadFeeLedger] =
    useState<EmployerFundLedger | null>(null);

  const [isCardIssueInfoOpen, setIsCardIssueInfoOpen] = useState(false);
  const [selectedCardIssueLedger, setSelectedCardIssueLedger] =
    useState<EmployerFundLedger | null>(null);

  const [isMonthlyUserFeeInfoOpen, setIsMonthlyUserFeeInfoOpen] =
    useState(false);
  const [selectedMonthlyUserFeeLedger, setSelectedMonthlyUserFeeLedger] =
    useState<EmployerFundLedger | null>(null);

  const renderLedgerMetadataByType = (ledger: EmployerFundLedger) => {
    switch (ledger.type) {
      case EmployerFundLedgerType.LOAD_FEE:
        return (
          <>
            <IconButton
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setIsLoadFeeInfoOpen(true);
                setSelectedLoadFeeLedger(ledger);
              }}
            >
              <MoreHorizontal />
            </IconButton>
          </>
        );
      case EmployerFundLedgerType.TRANSACTION:
        return (
          <>
            <IconButton
              onClick={() => {
                setIsTransactionInfoOpen(true);
                setSelectedTransactionId(
                  ledger.transactionLedgerMetadata?.originalTransactionId ??
                    null
                );
              }}
            >
              <MoreHorizontal />
            </IconButton>
          </>
        );
      case EmployerFundLedgerType.ISSUE_CARD_FEE ||
        EmployerFundLedgerType.RE_ISSUE_CARD_FEE:
        return (
          <>
            <IconButton
              onClick={() => {
                setIsCardIssueInfoOpen(true);
                setSelectedCardIssueLedger(ledger);
              }}
            >
              <MoreHorizontal />
            </IconButton>
          </>
        );
      case EmployerFundLedgerType.MONTHLY_USER_FEE:
        return (
          <>
            <IconButton
              onClick={() => {
                setIsMonthlyUserFeeInfoOpen(true);
                setSelectedMonthlyUserFeeLedger(ledger);
              }}
            >
              <MoreHorizontal />
            </IconButton>
          </>
        );
      default:
        return <Box>-</Box>;
    }
  };

  const columns: ColumnDef<EmployerFundLedger>[] = [
    {
      header: "Type",
      cell: ({ row }: { row: { original: EmployerFundLedger } }) => {
        var typeToDisplay =
          row.original.transactionLedgerMetadata?.type ?? row.original.type;
        const titleCase = toTitleCase(typeToDisplay);
        return <Box>{titleCase}</Box>;
      },
      enableColumnFilter: false
    },
    {
      header: "Date",
      meta: {
        filterType: "dateRange"
      },
      enableColumnFilter: false,
      cell: ({ row }) => {
        const formattedDate = formatDate(row.original.createdAt);
        return <Box>{formattedDate}</Box>;
      }
    },
    {
      cell: ({ row }) => (
        <Box>{formatCurrency(row.original.appliedAmount.amount)}</Box>
      ),
      header: "Amount",
      enableColumnFilter: false
    },
    {
      header: "User",
      cell: ({ row }: { row: { original: EmployerFundLedger } }) => {
        if (row.original.employeeMetadata) {
          return (
            <Box>
              {row.original?.employeeMetadata?.firstName}{" "}
              {row.original?.employeeMetadata?.lastName}
            </Box>
          );
        }
        return <Box>-</Box>;
      },
      enableColumnFilter: false
    },
    {
      header: "Info",
      cell: ({ row }: { row: { original: EmployerFundLedger } }) => {
        return renderLedgerMetadataByType(row.original);
      }
    }
  ];

  if (
    isLoading ||
    employer.isLoading ||
    employees.isLoading ||
    accounts.isLoading ||
    serviceSectors.isLoading
  )
    return <LoadingSpinner />;

  return (
    <>
      <Box display="flex" width="100%">
        {displayTransactionInfoMessage && (
          <Typography fontSize={14} color="grey">
            Only cleared transactions are deducted from your fund. Pending
            transactions may take up to 48 hours to clear.
          </Typography>
        )}
      </Box>

      <Table<EmployerFundLedger>
        data={ledgers}
        totalItems={totalItems}
        columns={columns}
        isServerSidePagination={true}
        paginationPage={page}
        paginationPageSize={pageSize}
        onPageChange={updatePage}
        onPageSizeChange={updatePageSize}
      />
      <TransactionInfoSideDrawer
        selectedTransactionId={selectedTransactionId}
        isOpen={isTransactionInfoOpen}
        setIsOpen={setIsTransactionInfoOpen}
      />
      <LoadFeeInfoSideDrawer
        ledger={selectedLoadFeeLedger}
        isOpen={isLoadFeeInfoOpen}
        setIsOpen={setIsLoadFeeInfoOpen}
      />
      <CardIssueInfoSideDrawer
        ledger={selectedCardIssueLedger}
        isOpen={isCardIssueInfoOpen}
        setIsOpen={setIsCardIssueInfoOpen}
      />
      <MonthlyUserFeeInfoSideDrawer
        ledger={selectedMonthlyUserFeeLedger}
        isOpen={isMonthlyUserFeeInfoOpen}
        setIsOpen={setIsMonthlyUserFeeInfoOpen}
      />
    </>
  );
};

export { EmployerFundLedgerTable };
